import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';
import { injectIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { completeOnboarding } from '../../util/api';
import { FaCheckCircle, FaTimesCircle, FaStar } from 'react-icons/fa';
import { AiOutlineCheckCircle } from 'react-icons/ai';
import { BsArrowDownCircleFill } from 'react-icons/bs';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  NamedRedirect,
  TopbarBlank,
  OnboardingAccountMenu,
} from '../../components';
import { TopbarContainer } from '../../containers';

import avatarLaura from '../../assets/freelancer-onboarding-avatar-1.webp';
import avatarMarkus from '../../assets/freelancer-onboarding-step-3.webp';
import illustrationBg from '../../assets/freelancer-onboarding-image.webp';
import css from './FreelancerOnboardingPage.css';

const CORRECT_ANSWERS = {
  1: 'A',
  2: 'A',
};

const FreelancerOnboardingPageComponent = props => {
  const { scrollingDisabled, intl, history, dispatch, currentUser } = props;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ 1: null, 2: null });
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const answered = answers[1] !== null || answers[2] !== null;
    if (!answered) return;
    if (!window.matchMedia('(max-width: 961px)').matches) return;
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  }, [answers]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
  }, []);

  // If onboarding is already done, redirect to dashboard
  const onboardingDone = currentUser?.attributes?.profile?.metadata?.onboardingDone;
  if (currentUser?.id && onboardingDone) {
    return <NamedRedirect name="DashboardPage" />;
  }

  const title = intl.formatMessage({ id: 'FreelancerOnboardingPage.title' });

  const handleAnswer = (quizStep, option) => {
    if (answers[quizStep] !== null) return;
    setAnswers(prev => ({ ...prev, [quizStep]: option }));
  };

  const getOptionState = (quizStep, option) => {
    const answer = answers[quizStep];
    if (answer === null) return 'default';
    if (option === CORRECT_ANSWERS[quizStep]) return 'correct';
    if (option === answer) return 'wrong';
    return 'default';
  };

  const canProceed = currentStep => {
    if (currentStep === 1) return answers[1] !== null;
    if (currentStep === 2) return answers[2] !== null;
    return true;
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleComplete = () => {
    const userId = currentUser?.id?.uuid;
    if (!userId || completing) return;
    setCompleting(true);
    completeOnboarding({ userId })
      .then(() => dispatch(fetchCurrentUser()))
      .then(() => history.push('/dashboard'))
      .catch(() => setCompleting(false));
  };

  // Render option indicator (circle with letter, or check/X icon)
  const renderOptionIndicator = (quizStep, option) => {
    const state = getOptionState(quizStep, option);
    if (state === 'correct') {
      return <FaCheckCircle className={css.optionIconCorrect} />;
    }
    if (state === 'wrong') {
      return <FaTimesCircle className={css.optionIconWrong} />;
    }
    return (
      <span className={css.optionCircle}>
        <span className={css.optionLetter}>{option}</span>
      </span>
    );
  };

  // Render a single quiz option
  const renderOption = (quizStep, option, textId) => {
    const state = getOptionState(quizStep, option);
    const isAnswered = answers[quizStep] !== null;
    const classNames = [
      css.option,
      state === 'correct' ? css.optionCorrect : '',
      state === 'wrong' ? css.optionWrong : '',
      isAnswered ? css.optionAnswered : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={classNames}
        onClick={() => handleAnswer(quizStep, option)}
        role="button"
        tabIndex={isAnswered ? -1 : 0}
        onKeyDown={e => {
          if (!isAnswered && (e.key === 'Enter' || e.key === ' ')) {
            handleAnswer(quizStep, option);
          }
        }}
      >
        {renderOptionIndicator(quizStep, option)}
        <span className={css.optionText}>
          <FormattedMessage id={textId} />
        </span>
      </div>
    );
  };

  // Render answer feedback
  const renderFeedback = quizStep => {
    const answer = answers[quizStep];
    if (answer === null) return null;

    const isCorrect = answer === CORRECT_ANSWERS[quizStep];
    if (isCorrect) {
      return (
        <p className={css.feedbackCorrect}>
          <FormattedMessage id={`FreelancerOnboardingPage.step${quizStep + 1}Correct`} />
        </p>
      );
    }
    return (
      <p className={css.feedbackWrong}>
        <span className={css.feedbackWrongTitle}>
          <FormattedMessage id={`FreelancerOnboardingPage.step${quizStep + 1}WrongTitle`} />
        </span>{' '}
        <FormattedMessage id={`FreelancerOnboardingPage.step${quizStep + 1}WrongExplanation`} />
      </p>
    );
  };

  // Navigation bar with Back, dots, Next/Complete
  const renderNavigation = () => {
    const canGoNext = canProceed(step);
    const isLastStep = step === 3;

    return (
      <div className={css.navigation}>
        <button className={css.backButton} onClick={handleBack} type="button">
          <FormattedMessage id="FreelancerOnboardingPage.backButton" />
        </button>

        <div className={css.stepDots}>
          {[1, 2, 3].map(i => (
            <span key={i} className={i === step ? css.dotActive : css.dot} />
          ))}
        </div>

        {isLastStep ? (
          <button
            className={css.completeButton}
            onClick={handleComplete}
            disabled={completing}
            type="button"
          >
            {completing ? (
              <FormattedMessage id="FreelancerOnboardingPage.completingButton" />
            ) : (
              <FormattedMessage id="FreelancerOnboardingPage.completeButton" />
            )}
          </button>
        ) : (
          <button
            className={canGoNext ? css.nextButton : css.nextButtonDisabled}
            onClick={canGoNext ? handleNext : undefined}
            disabled={!canGoNext}
            type="button"
          >
            <FormattedMessage id="FreelancerOnboardingPage.nextButton" />
          </button>
        )}
      </div>
    );
  };

  // ==================== STEP RENDERERS ====================

  // Step 1 - Intro
  const renderStep0 = () => (
    <div className={css.introCard}>
      <span className={css.accountCreatedBadge}>
        <AiOutlineCheckCircle className={css.accountCreatedBadgeIcon} />
        <FormattedMessage id="FreelancerOnboardingPage.accountCreatedBadge" />
      </span>
      <h1 className={css.introTitle}>
        <FormattedMessage
          id="FreelancerOnboardingPage.step1Title"
          values={{
            highlight: (
              <span className={css.introHighlight}>
                <FormattedMessage id="FreelancerOnboardingPage.step1TitleHighlight" />
              </span>
            ),
          }}
        />
      </h1>
      <button className={css.introButton} onClick={() => setStep(1)} type="button">
        <FormattedMessage id="FreelancerOnboardingPage.step1Button" />
      </button>
      <p className={css.introDuration}>
        <FormattedMessage
          id="FreelancerOnboardingPage.step1Duration"
          values={{
            duration: (
              <span className={css.introDurationHighlight}>
                <FormattedMessage id="FreelancerOnboardingPage.step1DurationValue" />
              </span>
            ),
          }}
        />
      </p>
    </div>
  );

  // Step 2 - How Freedomly Works
  const renderStep1 = () => (
    <div className={css.quizCard}>
      <div className={css.quizColumns}>
        <div className={css.leftColumn}>
          <h2 className={css.sectionTitle}>
            <FormattedMessage id="FreelancerOnboardingPage.step2Title" />
          </h2>
          <div className={css.stepsList}>
            {[1, 2, 3].map(i => (
              <div key={i} className={css.stepItem}>
                <span className={css.stepNumberBadge}>
                  <span className={css.stepNumberText}>{i}</span>
                </span>
                <span className={css.stepItemText}>
                  <FormattedMessage id={`FreelancerOnboardingPage.step2Item${i}`} />
                </span>
              </div>
            ))}
          </div>
          {renderStep2Illustration()}
        </div>

        <div className={css.columnDivider} />

        <div className={css.rightColumn}>
          <div className={css.questionHeader}>
            <span className={css.answerBadge}>
              <BsArrowDownCircleFill className={css.answerBadgeIcon} />
              <FormattedMessage id="FreelancerOnboardingPage.answerBelowBadge" />
            </span>
            <h3 className={css.questionText}>
              <FormattedMessage id="FreelancerOnboardingPage.step2Question" />
            </h3>
          </div>
          <div className={css.optionsList}>
            {renderOption(1, 'A', 'FreelancerOnboardingPage.step2OptionA')}
            {renderOption(1, 'B', 'FreelancerOnboardingPage.step2OptionB')}
          </div>
          {renderFeedback(1)}
        </div>
      </div>
      {renderNavigation()}
    </div>
  );

  // Step 3 - Experience Matters
  const renderStep2 = () => (
    <div className={css.quizCard}>
      <div className={css.quizColumns}>
        <div className={css.leftColumn}>
          <h2 className={css.sectionTitle}>
            <FormattedMessage id="FreelancerOnboardingPage.step3Title" />
          </h2>
          <p className={css.sectionDescription}>
            <FormattedMessage id="FreelancerOnboardingPage.step3Description" />
          </p>
          <p className={css.highlightText}>
            <FormattedMessage id="FreelancerOnboardingPage.step3Highlight" />
          </p>
          <div className={css.checklistContainer}>
            <div className={css.checklistPill}>
              <AiOutlineCheckCircle className={css.checklistIcon} />
              <span>
                <FormattedMessage id="FreelancerOnboardingPage.step3Check1" />
              </span>
            </div>
            <div className={css.checklistRow}>
              <div className={css.checklistPill}>
                <AiOutlineCheckCircle className={css.checklistIcon} />
                <span>
                  <FormattedMessage id="FreelancerOnboardingPage.step3Check2" />
                </span>
              </div>
              <div className={css.checklistPill}>
                <AiOutlineCheckCircle className={css.checklistIcon} />
                <span>
                  <FormattedMessage id="FreelancerOnboardingPage.step3Check3" />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={css.columnDivider} />

        <div className={css.rightColumn}>
          <div className={css.questionHeader}>
            <span className={css.answerBadge}>
              <BsArrowDownCircleFill className={css.answerBadgeIcon} />
              <FormattedMessage id="FreelancerOnboardingPage.answerBelowBadge" />
            </span>
            <h3 className={css.questionText}>
              <FormattedMessage id="FreelancerOnboardingPage.step3Question" />
            </h3>
          </div>
          <div className={css.optionsList}>
            {renderOption(2, 'A', 'FreelancerOnboardingPage.step3OptionA')}
            {renderOption(2, 'B', 'FreelancerOnboardingPage.step3OptionB')}
          </div>
          {renderFeedback(2)}
        </div>
      </div>
      {renderNavigation()}
    </div>
  );

  // Step 4 - Profile Grows
  const renderStep3 = () => (
    <div className={css.quizCard}>
      <div className={css.step4Columns}>
        {renderStep4Illustration()}
        <div className={css.step4TextContent}>
          <h2 className={css.sectionTitle}>
            <FormattedMessage id="FreelancerOnboardingPage.step4Title" />
          </h2>
          <p className={css.step4Description}>
            <FormattedMessage
              id="FreelancerOnboardingPage.step4Description"
              values={{
                bold: (
                  <strong className={css.step4DescriptionBold}>
                    <FormattedMessage id="FreelancerOnboardingPage.step4DescriptionBold" />
                  </strong>
                ),
              }}
            />
          </p>
        </div>
      </div>
      {renderNavigation()}
    </div>
  );

  // ==================== ILLUSTRATIONS ====================

  // Decorative illustration for step 2 - matching visualization
  const renderStep2Illustration = () => (
    <div
      className={css.illustrationContainer}
      style={{ backgroundImage: `url(${illustrationBg})` }}
    >
      <div className={css.illustrationInner}>
        <div className={css.miniCardOpportunity}>
          <span className={css.miniCardLabel}>
            <FormattedMessage id="FreelancerOnboardingPage.step2IllustrationOpportunityLabel" />
          </span>
          <span className={css.miniCardTitle}>
            <FormattedMessage id="FreelancerOnboardingPage.step2IllustrationOpportunityTitle" />
          </span>
          <div className={css.miniCardLines}>
            <div className={`${css.miniCardLine} ${css.miniCardLineFull}`} />
            <div className={`${css.miniCardLine} ${css.miniCardLineMedium}`} />
            <div className={`${css.miniCardLine} ${css.miniCardLineShort}`} />
          </div>
        </div>
        <div className={css.miniCardConnector}>
          <div className={css.connectorLine} />
        </div>
        <div className={css.miniCardFreelancer}>
          <div className={css.freelancerHeader}>
            <img src={avatarLaura} alt="" className={css.freelancerAvatar} />
            <div className={css.freelancerInfo}>
              <span className={css.freelancerName}>
                <FormattedMessage id="FreelancerOnboardingPage.step2IllustrationFreelancerName" />
              </span>
              <span className={css.freelancerRole}>
                <FormattedMessage id="FreelancerOnboardingPage.step2IllustrationFreelancerRole" />
              </span>
            </div>
          </div>
          <div className={css.matchBadge}>
            <span className={css.matchDot} />
            <span className={css.matchText}>
              <FormattedMessage id="FreelancerOnboardingPage.step2IllustrationMatchText" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Decorative illustration for step 4 - profile card
  const renderStep4Illustration = () => (
    <div className={css.profileIllustration}>
      <div className={css.profileCardContainer}>
        <div className={css.profileCard}>
          <img src={avatarMarkus} alt="" className={css.profileAvatarLarge} />
          <span className={css.profileName}>
            <FormattedMessage id="FreelancerOnboardingPage.step4ProfileName" />
          </span>
          <span className={css.profileTitle}>
            <FormattedMessage id="FreelancerOnboardingPage.step4ProfileTitle" />
          </span>
          <div className={css.profileRating}>
            <FaStar className={css.starIcon} />
            <span className={css.ratingValue}>5.0</span>
            <span className={css.ratingCount}>(10)</span>
          </div>
        </div>
        <div className={css.profileBadges}>
          <div className={css.profileBadge}>
            <AiOutlineCheckCircle className={css.badgeIcon} />
            <span>
              <FormattedMessage id="FreelancerOnboardingPage.step4BadgeVerifiedExpert" />
            </span>
          </div>
          <div className={css.profileBadge}>
            <AiOutlineCheckCircle className={css.badgeIcon} />
            <span>
              <FormattedMessage id="FreelancerOnboardingPage.step4BadgeRepeatingBuyers" />
            </span>
          </div>
          <div className={css.profileBadge}>
            <span className={css.earningsAmount}>€75K+</span>
            <span>
              <FormattedMessage id="FreelancerOnboardingPage.step4BadgeEarnings" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== MAIN RENDER ====================

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarBlank>
            <OnboardingAccountMenu />
          </TopbarBlank>
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.root}>
            {step === 0 && renderStep0()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  currentUser: state.user.currentUser,
});

const FreelancerOnboardingPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(FreelancerOnboardingPageComponent);

export default FreelancerOnboardingPage;
